import React, { useState } from 'react';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import ExplorationFieldCard from './components/ExplorationFieldCard';
import ProgressionPanel from './components/ProgressionPanel';
import AchievementMilestone from './components/AchievementMilestone';
import FieldDetailsModal from './components/FieldDetailsModal';
import UnlockCelebration from './components/UnlockCelebration';

const FieldExplorationHub = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedField, setSelectedField] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);

  // Mock user progression data
  const userProgress = {
    totalPoints: 2450,
    explorationLevel: 3,
    fieldsUnlocked: 8,
    totalFields: 24,
    completedDiscoveries: 5,
    nextMilestone: 3000,
    pointsToNextMilestone: 550
  };

  // Mock field categories
  const categories = [
    { id: 'all', name: 'Tous les Domaines', icon: 'Grid3x3', count: 24 },
    { id: 'sciences', name: 'Sciences & Tech', icon: 'Atom', count: 8 },
    { id: 'business', name: 'Commerce & Gestion', icon: 'Briefcase', count: 6 },
    { id: 'health', name: 'Santé & Médecine', icon: 'Heart', count: 5 },
    { id: 'arts', name: 'Arts & Humanités', icon: 'Palette', count: 5 }
  ];

  // Mock exploration fields with lock status
  const explorationFields = [
    {
      id: 1,
      name: 'Informatique & Programmation',
      category: 'sciences',
      description: 'Développement logiciel, intelligence artificielle, cybersécurité et systèmes informatiques',
      isUnlocked: true,
      requiredPoints: 0,
      explorationProgress: 100,
      careerHighlights: ['Développeur Full-Stack', 'Data Scientist', 'Ingénieur DevOps'],
      averageSalary: '800K - 2M FCFA/mois',
      demandLevel: 'high',
      skillsRequired: ['Logique', 'Mathématiques', 'Résolution de problèmes'],
      rewardPoints: 200,
      icon: 'Code',
      color: 'blue'
    },
    {
      id: 2,
      name: 'Médecine Générale',
      category: 'health',
      description: 'Formation médicale complète pour devenir médecin généraliste ou spécialiste',
      isUnlocked: true,
      requiredPoints: 0,
      explorationProgress: 75,
      careerHighlights: ['Médecin Généraliste', 'Chirurgien', 'Pédiatre'],
      averageSalary: '1M - 3M FCFA/mois',
      demandLevel: 'high',
      skillsRequired: ['Biologie', 'Chimie', 'Empathie'],
      rewardPoints: 250,
      icon: 'Stethoscope',
      color: 'green'
    },
    {
      id: 3,
      name: 'Gestion d\'Entreprise',
      category: 'business',
      description: 'Management, stratégie d\'entreprise, finance et entrepreneuriat',
      isUnlocked: true,
      requiredPoints: 0,
      explorationProgress: 60,
      careerHighlights: ['Directeur Général', 'Consultant', 'Entrepreneur'],
      averageSalary: '700K - 2.5M FCFA/mois',
      demandLevel: 'high',
      skillsRequired: ['Leadership', 'Communication', 'Analyse'],
      rewardPoints: 180,
      icon: 'TrendingUp',
      color: 'amber'
    },
    {
      id: 4,
      name: 'Génie Civil',
      category: 'sciences',
      description: 'Conception et construction d\'infrastructures, bâtiments et ouvrages d\'art',
      isUnlocked: true,
      requiredPoints: 500,
      explorationProgress: 40,
      careerHighlights: ['Ingénieur Structure', 'Chef de Projet BTP', 'Architecte'],
      averageSalary: '600K - 1.8M FCFA/mois',
      demandLevel: 'high',
      skillsRequired: ['Mathématiques', 'Physique', 'Dessin technique'],
      rewardPoints: 200,
      icon: 'Building2',
      color: 'blue'
    },
    {
      id: 5,
      name: 'Design Graphique',
      category: 'arts',
      description: 'Création visuelle, branding, design digital et communication graphique',
      isUnlocked: true,
      requiredPoints: 800,
      explorationProgress: 20,
      careerHighlights: ['Designer UI/UX', 'Directeur Artistique', 'Illustrateur'],
      averageSalary: '400K - 1.5M FCFA/mois',
      demandLevel: 'medium',
      skillsRequired: ['Créativité', 'Sens artistique', 'Logiciels design'],
      rewardPoints: 150,
      icon: 'Palette',
      color: 'purple'
    },
    {
      id: 6,
      name: 'Pharmacie',
      category: 'health',
      description: 'Sciences pharmaceutiques, médicaments et soins de santé',
      isUnlocked: false,
      requiredPoints: 1200,
      explorationProgress: 0,
      careerHighlights: ['Pharmacien d\'Officine', 'Pharmacien Hospitalier', 'Chercheur'],
      averageSalary: '800K - 2M FCFA/mois',
      demandLevel: 'high',
      skillsRequired: ['Chimie', 'Biologie', 'Précision'],
      rewardPoints: 220,
      icon: 'Pill',
      color: 'green'
    },
    {
      id: 7,
      name: 'Marketing Digital',
      category: 'business',
      description: 'Stratégies marketing en ligne, réseaux sociaux et e-commerce',
      isUnlocked: false,
      requiredPoints: 1500,
      explorationProgress: 0,
      careerHighlights: ['Social Media Manager', 'Growth Hacker', 'Content Strategist'],
      averageSalary: '500K - 1.8M FCFA/mois',
      demandLevel: 'high',
      skillsRequired: ['Communication', 'Créativité', 'Analyse de données'],
      rewardPoints: 180,
      icon: 'Megaphone',
      color: 'amber'
    },
    {
      id: 8,
      name: 'Intelligence Artificielle',
      category: 'sciences',
      description: 'Machine learning, deep learning et systèmes intelligents',
      isUnlocked: false,
      requiredPoints: 2000,
      explorationProgress: 0,
      careerHighlights: ['ML Engineer', 'AI Researcher', 'Data Scientist'],
      averageSalary: '1M - 3M FCFA/mois',
      demandLevel: 'high',
      skillsRequired: ['Mathématiques avancées', 'Programmation', 'Statistiques'],
      rewardPoints: 300,
      icon: 'Brain',
      color: 'blue'
    },
    {
      id: 9,
      name: 'Architecture',
      category: 'arts',
      description: 'Conception architecturale, urbanisme et design d\'espaces',
      isUnlocked: false,
      requiredPoints: 1800,
      explorationProgress: 0,
      careerHighlights: ['Architecte', 'Urbaniste', 'Designer d\'Intérieur'],
      averageSalary: '700K - 2.5M FCFA/mois',
      demandLevel: 'medium',
      skillsRequired: ['Créativité', 'Dessin', 'Vision spatiale'],
      rewardPoints: 200,
      icon: 'Home',
      color: 'purple'
    },
    {
      id: 10,
      name: 'Droit des Affaires',
      category: 'business',
      description: 'Droit commercial, contrats et conseil juridique aux entreprises',
      isUnlocked: false,
      requiredPoints: 2200,
      explorationProgress: 0,
      careerHighlights: ['Avocat d\'Affaires', 'Juriste d\'Entreprise', 'Notaire'],
      averageSalary: '800K - 3M FCFA/mois',
      demandLevel: 'medium',
      skillsRequired: ['Analyse', 'Argumentation', 'Mémoire'],
      rewardPoints: 220,
      icon: 'Scale',
      color: 'amber'
    },
    {
      id: 11,
      name: 'Psychologie Clinique',
      category: 'health',
      description: 'Santé mentale, thérapie et accompagnement psychologique',
      isUnlocked: false,
      requiredPoints: 2500,
      explorationProgress: 0,
      careerHighlights: ['Psychologue Clinicien', 'Thérapeute', 'Conseiller'],
      averageSalary: '500K - 1.5M FCFA/mois',
      demandLevel: 'medium',
      skillsRequired: ['Empathie', 'Écoute', 'Analyse comportementale'],
      rewardPoints: 200,
      icon: 'Users',
      color: 'green'
    },
    {
      id: 12,
      name: 'Cinéma & Production',
      category: 'arts',
      description: 'Réalisation, production audiovisuelle et storytelling visuel',
      isUnlocked: false,
      requiredPoints: 2800,
      explorationProgress: 0,
      careerHighlights: ['Réalisateur', 'Producteur', 'Monteur'],
      averageSalary: '600K - 2M FCFA/mois',
      demandLevel: 'low',
      skillsRequired: ['Créativité', 'Narration', 'Technique audiovisuelle'],
      rewardPoints: 180,
      icon: 'Video',
      color: 'purple'
    }
  ];

  // Mock achievement milestones
  const achievementMilestones = [
    {
      id: 1,
      name: 'Explorateur Débutant',
      description: 'Débloquez votre premier domaine',
      icon: 'Compass',
      isEarned: true,
      earnedDate: '15 Jan 2026',
      requiredPoints: 0,
      rewards: ['Badge Explorateur', '+50 points bonus']
    },
    {
      id: 2,
      name: 'Découvreur Actif',
      description: 'Explorez 5 domaines différents',
      icon: 'Map',
      isEarned: true,
      earnedDate: '28 Jan 2026',
      requiredPoints: 1000,
      rewards: ['Badge Découvreur', 'Accès interviews experts']
    },
    {
      id: 3,
      name: 'Spécialiste de Domaine',
      description: 'Complétez l\'exploration d\'un domaine à 100%',
      icon: 'Award',
      isEarned: true,
      earnedDate: '05 Fév 2026',
      requiredPoints: 1500,
      rewards: ['Certification Spécialiste', 'Contenu premium débloqué']
    },
    {
      id: 4,
      name: 'Chercheur de Carrière',
      description: 'Débloquez 10 domaines',
      icon: 'Target',
      isEarned: false,
      requiredPoints: 3000,
      rewards: ['Badge Chercheur', 'Expériences virtuelles']
    },
    {
      id: 5,
      name: 'Maître Explorateur',
      description: 'Explorez tous les domaines disponibles',
      icon: 'Trophy',
      isEarned: false,
      requiredPoints: 5000,
      rewards: ['Badge Maître', 'Accès VIP plateforme']
    }
  ];

  const handleFieldClick = (field) => {
    if (field?.isUnlocked) {
      setSelectedField(field);
    } else {
      // Show unlock requirements
      alert(`Débloquez ce domaine en gagnant ${field?.requiredPoints - userProgress?.totalPoints} points supplémentaires!`);
    }
  };

  const handleUnlockField = (field) => {
    // Simulate field unlock
    setCelebrationData({
      fieldName: field?.name,
      rewardPoints: field?.rewardPoints,
      icon: field?.icon
    });
    setShowCelebration(true);
    setTimeout(() => setShowCelebration(false), 3000);
  };

  const filteredFields = selectedCategory === 'all' 
    ? explorationFields 
    : explorationFields?.filter(field => field?.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <main className="content-area">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          {/* Header Section */}
          <div className="mb-8 md:mb-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-2">
                  Hub d'Exploration de Domaines
                </h1>
                <p className="text-base md:text-lg text-muted-foreground">
                  Découvrez des carrières passionnantes et débloquez du contenu exclusif
                </p>
              </div>
              <div className="flex items-center gap-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl shadow-lg">
                <Icon name="Zap" size={24} />
                <div>
                  <p className="text-sm opacity-90">Points d'Exploration</p>
                  <p className="text-2xl font-bold">{userProgress?.totalPoints}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Progression Panel */}
          <ProgressionPanel userProgress={userProgress} />

          {/* Category Filter */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Icon name="Filter" size={20} className="text-primary" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Catégories de Domaines
              </h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {categories?.map((category) => (
                <button
                  key={category?.id}
                  onClick={() => setSelectedCategory(category?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                    selectedCategory === category?.id
                      ? 'bg-primary text-white border-primary shadow-md'
                      : 'bg-card text-foreground border-border hover:border-primary hover:shadow-sm'
                  }`}
                >
                  <Icon name={category?.icon} size={18} />
                  <span className="font-medium">{category?.name}</span>
                  <span className="text-xs opacity-75">({category?.count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Field Map Grid */}
          <div className="mb-8 md:mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Icon name="Map" size={24} className="text-primary" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Carte d'Exploration Interactive
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
              {filteredFields?.map((field) => (
                <ExplorationFieldCard
                  key={field?.id}
                  field={field}
                  userPoints={userProgress?.totalPoints}
                  onClick={() => handleFieldClick(field)}
                  onUnlock={() => handleUnlockField(field)}
                />
              ))}
            </div>
          </div>

          {/* Achievement Milestones */}
          <div className="mb-8 md:mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Icon name="Trophy" size={24} className="text-primary" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Jalons de Réussite
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5">
              {achievementMilestones?.map((milestone) => (
                <AchievementMilestone
                  key={milestone?.id}
                  milestone={milestone}
                  userPoints={userProgress?.totalPoints}
                  onUnlock={() => {}}
                />
              ))}
            </div>
          </div>

          {/* How to Earn Points */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6 md:p-8 border border-border">
            <div className="flex items-center gap-3 mb-6">
              <Icon name="Lightbulb" size={24} className="text-primary" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Comment Gagner des Points
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-border">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Icon name="ClipboardList" size={20} className="text-blue-600" />
                  </div>
                  <span className="text-2xl font-bold text-blue-600">+150</span>
                </div>
                <h3 className="font-semibold text-foreground mb-1">Compléter un Quiz</h3>
                <p className="text-sm text-muted-foreground">Répondez aux quiz d'orientation</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-border">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <Icon name="MessageSquare" size={20} className="text-green-600" />
                  </div>
                  <span className="text-2xl font-bold text-green-600">+100</span>
                </div>
                <h3 className="font-semibold text-foreground mb-1">Chat IA Actif</h3>
                <p className="text-sm text-muted-foreground">Discutez avec l'assistant IA</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-border">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <Icon name="Eye" size={20} className="text-purple-600" />
                  </div>
                  <span className="text-2xl font-bold text-purple-600">+50</span>
                </div>
                <h3 className="font-semibold text-foreground mb-1">Explorer un Domaine</h3>
                <p className="text-sm text-muted-foreground">Consultez les détails complets</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-border">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} className="text-amber-600" />
                  </div>
                  <span className="text-2xl font-bold text-amber-600">+200</span>
                </div>
                <h3 className="font-semibold text-foreground mb-1">Exploration Complète</h3>
                <p className="text-sm text-muted-foreground">Atteignez 100% dans un domaine</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Field Details Modal */}
      {selectedField && (
        <FieldDetailsModal
          field={selectedField}
          onClose={() => setSelectedField(null)}
        />
      )}

      {/* Unlock Celebration */}
      {showCelebration && celebrationData && (
        <UnlockCelebration data={celebrationData} />
      )}
    </div>
  );
};

export default FieldExplorationHub;